import { useAtomValue } from "jotai";
import type { NextPageContext, PageProps } from "next";
import { useEffect } from "react";
import { graphql, usePreloadedQuery } from "react-relay";
import type { RelayComponent } from "relay-nextjs";
import { withRelay } from "relay-nextjs";

import { parseQueryString } from "shared/lib/utils";

import type { AppBarVariant } from "scmp-app/components/app-bar/types";
import type { Application } from "scmp-app/components/app-initializer/helpers";
import { FallbackLoading } from "scmp-app/components/fallback-loading";
import { DefaultPages } from "scmp-app/components/pages/default-pages";
import { DefaultPageSeo } from "scmp-app/components/pages/default-pages/default-pages-seo";
import { trackingAtom } from "scmp-app/components/tracking";
import { createClientEnvironment } from "scmp-app/lib/relay/environment.client";
import type { pagePageQuery } from "scmp-app/queries/__generated__/pagePageQuery.graphql";

const query = graphql`
  query pagePageQuery($entityId: String!) {
    page(filter: { entityId: $entityId }) {
      ...defaultPagesPage
      ...defaultPagesSeoPage
    }
  }
`;
const PagePage: RelayComponent<PageProps, pagePageQuery> = ({ preloadedQuery }) => {
  const queryReference = usePreloadedQuery(query, preloadedQuery);
  const { trackPageView } = useAtomValue(trackingAtom);

  useEffect(() => {
    trackPageView?.();
  }, [trackPageView]);

  return (
    <>
      <DefaultPageSeo reference={queryReference.page} />
      <DefaultPages reference={queryReference.page} />
    </>
  );
};

PagePage.displayName = "PagePage";

const getPageProps = (context: NextPageContext) => {
  const appBarVariant = context.query.appBarVariant as AppBarVariant;
  const application = context.query.application as Application;

  const pageProps: PageProps = {
    appBarConfiguration: {
      hasMobileAd: false,
      variant: appBarVariant,
    },
    appInitializerConfiguration: {
      application,
    },
  };

  return pageProps;
};

export default withRelay(PagePage, query, {
  clientSideProps: context => getPageProps(context),
  createClientEnvironment: () => createClientEnvironment(),
  createServerEnvironment: async context => {
    const { createServerEnvironment } = await import("scmp-app/lib/relay/environment.server");
    return createServerEnvironment(context.req?.cookies);
  },
  fallback: <FallbackLoading />,
  serverSideProps: context => Promise.resolve(getPageProps(context)),
  variablesFromContext(context) {
    return { entityId: parseQueryString(context.query.entityId) ?? "" };
  },
});
